// EventList.tsx - Updated with sorting
import { useLocalization } from '@ais-ui/locale-management';
import {
  Accordion,
  CardContent,
  CardDescription,
  CardFooter,
  InfiniteScroll,
  Input,
  LoadingSpinner,
  ScrollArea,
  TrailingDateTimePickerWithRange,
} from '@ais-ui/ui-core';
import { useTimeZoneContext } from '@ais-ui/utils';
import {
  DetectedEvent,
  useGetEvents,
  useGetEventsForPeriod,
} from '@ais-ui/websockets';
import React, { useEffect, useMemo, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { EventActionsMenu } from './EventActionsMenu';
import {
  CombinedGroundingEventAndConfiguration,
  GroundingEventItem,
  useGetGroundingEventsAndConfigurations,
} from './Detectors/Grounding/GroundingEvent';
import { useEventFilters } from './useEventFilters';
import { EventFilter } from './EventFilter';
import { EventSort } from './EventSort';
import { NoResultsEmptyStateList } from '../../components/EmptyList';
import { EventItem } from './EventItem';

interface EventListProps {
  date: DateRange | undefined;
  setDate: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
  isTrailing: boolean;
  setIsTrailing: React.Dispatch<React.SetStateAction<boolean>>;
  defaultLookBackTime: number;
}

export const EventList = ({
  date,
  setDate,
  isTrailing,
  setIsTrailing,
  defaultLookBackTime,
}: EventListProps) => {
  const { messages } = useLocalization();
  const { timeZone } = useTimeZoneContext();
  const { userDateFormat } = useDateTimeFormat();
  const [search, setSearch] = useState<string>('');
  const [events, setEvents] = useState<DetectedEvent[]>([]);
  const [groundingEvents, setGroundingEvents] = useState<
    CombinedGroundingEventAndConfiguration[]
  >([]);

  const [queryDate, setQueryDate] = useState(date);

  useGetEvents();

  const { data, isLoading, isFetching } = useGetEventsForPeriod(
    queryDate?.from,
    queryDate?.to
  );

  const { groundingEventsAndConfigurations, groundingIsLoading } =
    useGetGroundingEventsAndConfigurations(queryDate);

  useEffect(() => {
    if (isTrailing) {
      // In trailing mode, only update if we're not currently fetching
      if (!isFetching) {
        setQueryDate(date);
      }
    } else {
      // In normal mode, update immediately
      setQueryDate(date);
    }
  }, [date, isTrailing, isFetching]);

  useEffect(() => {
    // When trailing is enabled, we don't want to set the events empty on loading
    //  as it is disruptive for the user
    if (isTrailing) {
      if (isLoading) {
        return;
      }
    }

    setEvents(data?.events || []);
  }, [data, isLoading, isTrailing]);

  useEffect(() => {
    if (isTrailing) {
      if (groundingIsLoading) {
        return;
      }
    }

    setGroundingEvents(groundingEventsAndConfigurations || []);
  }, [groundingEventsAndConfigurations, groundingIsLoading, isTrailing]);

  const {
    combinedEvents,
    eventFilters,
    setEventFilters,
    sortConfig,
    setSortConfig,
  } = useEventFilters(events, groundingEvents, search);

  const isLoadingAnyEvents = useMemo(() => {
    if (!queryDate) {
      return false;
    }
    // Only show loading spinner on initial load
    return (isLoading || groundingIsLoading) && events.length === 0;
  }, [queryDate, isLoading, groundingIsLoading, events.length]);

  return (
    <>
      <CardContent className="space-y-4">
        <CardDescription>{messages.eventsListSubtitle}</CardDescription>
        <TrailingDateTimePickerWithRange
          date={date}
          setDate={setDate}
          timeZone={timeZone}
          dateTimeFormat={userDateFormat}
          entity="event"
          isTrailing={isTrailing}
          setIsTrailing={setIsTrailing}
          lookBackTimeInMinutes={defaultLookBackTime}
        />
        <div className="flex gap-2">
          <Input
            id="search"
            placeholder="Find events..."
            className="h-10 flex-1"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
          />
          <EventSort sortConfig={sortConfig} setSortConfig={setSortConfig} />
          <EventFilter
            eventFilters={eventFilters}
            setEventFilters={setEventFilters}
          />
        </div>
      </CardContent>
      <CardContent className="flex-1 flex flex-col overflow-hidden">
        {combinedEvents.length === 0 && isLoadingAnyEvents && (
          <LoadingSpinner />
        )}
        {combinedEvents.length === 0 && !isLoadingAnyEvents && (
          <NoResultsEmptyStateList
            entity="event"
            srcImage="empty_list_bro.png"
          />
        )}
        {combinedEvents.length > 0 && (
          <Accordion
            className="flex-1 flex flex-col overflow-hidden"
            type="single"
            collapsible
          >
            <InfiniteScroll
              items={combinedEvents}
              limit={20}
              observeResize={true}
              className="flex-grow p-0 overflow-y-auto border rounded-lg"
              renderItem={(combinedEvent) =>
                combinedEvent.type === 'regular' ? (
                  <EventItem
                    key={`regular-${combinedEvent.id}`}
                    event={combinedEvent.event as DetectedEvent}
                  />
                ) : (
                  <GroundingEventItem
                    key={`grounding-${combinedEvent.id}`}
                    event={
                      (
                        combinedEvent.event as CombinedGroundingEventAndConfiguration
                      ).event
                    }
                    configuration={
                      (
                        combinedEvent.event as CombinedGroundingEventAndConfiguration
                      ).configuration
                    }
                  />
                )
              }
            />
          </Accordion>
        )}
      </CardContent>
      <CardFooter className="flex flex-grow-0 flex-col items-end min-h-52">
        <EventActionsMenu start={date?.from} end={date?.to} />
      </CardFooter>
    </>
  );
};
